/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import type { MappingIssue } from './mapping-issues';
import type { FieldFinding } from '../inputs/FormField';
import {
  DEMAND_SOURCE_PRICE_TYPE_LABELS,
  describeDemandSourceCost,
  describeDemandSourcePrice,
  describeDemandSourceWeight,
  readDemandSourceCost,
  readDemandSourcePrice,
  type DemandSourceCost,
  type DemandSourceCostType,
  type DemandSourcePrice,
  type DemandSourcePriceType,
  type StoredDemandSourceTerms,
} from '@demand-source-cost';
import {
  BOUND_MEMBERS,
  BOUND_RULES,
  boundsValueProblem,
  boundsValueFinding,
  combineBounds,
  describeLoosening,
  resolveTerms,
  STANDARD_TERMS_KEY,
  STANDARD_TERMS_NAME,
  statedBounds,
  TERM_MEMBERS,
  termsValueProblem,
  type BoundMember,
  type PartialBounds,
  type PartialTerms,
  type ResolvedBoundValue,
  type ResolvedTerms,
  type TermMember,
  type TermsTierRef,
} from '@artifact-terms';

/**
 * The Terms as the form holds them (`3c-0`, `#1341`; price, weight and max rounds since `3c-i`).
 * Every number may be empty, since an empty box is unfinished rather than invalid (`NumberField`).
 */
export interface TermsFormValue {
  /**
   * What this tier **states** about being required — `true`, `false`, or nothing (`3c-ii`, `#1398`).
   *
   * Three answers, because *not required* and *says nothing* are different statements once a partner
   * can state one: a source under a partner that requires it may say `false` to override, and a source
   * that says nothing takes whatever is beneath it. A boolean with a *was it stated* flag beside it
   * could turn a stated `true` off but never state `false` from nothing, which is the override `ST4`
   * promises.
   */
  required: boolean | undefined;
  cost: {
    type: DemandSourceCostType;
    value: number | undefined;
    currency: string;
  };
  /** What the auction uses as the gross value — a floor or a fixed price (`ST3`). */
  price: {
    type: DemandSourcePriceType;
    value: number | undefined;
    currency: string;
  };
  /** The score boost, a signed percent (`#937`). Empty is no boost. */
  weight: number | undefined;
  /** How many times req-loop may call the source per pod. Empty is no cap. */
  maxFetchRounds: number | undefined;
  /**
   * **The ad restrictions this tier states** (`5c-i`, `#1321`) — on the form rather than beside it, so
   * they travel on the one value the box hands to `onChange` and a save cannot drop them (*a property
   * that must survive a save travels on what the editor hands to `onChange`*).
   */
  restrictions: RestrictionsFormValue;
}

/**
 * The restrictions as the form holds them. An empty list and an empty box both mean *this tier states
 * nothing*; in particular an empty **allow-list** here is *no allow-list*, never *allow nothing* — an
 * operator clearing the box means the first, and nobody authors the second on purpose.
 */
export interface RestrictionsFormValue {
  minDurationSeconds: number | undefined;
  maxDurationSeconds: number | undefined;
  allowIabCategories: readonly string[];
  excludeIabCategories: readonly string[];
  excludeAdvertisers: readonly string[];
  excludeDomains: readonly string[];
  /** Three answers, like `required`: allowed, not allowed, or this tier says nothing. */
  allowRepeatAds: boolean | undefined;
}

/** What a save sends. A blank member is omitted: the source states nothing about it. */
export interface DemandSourceTermsPayload {
  required?: boolean;
  cost?: DemandSourceCost;
  price?: DemandSourcePrice;
  weight?: number;
  maxFetchRounds?: number;
}

/**
 * Hydrates the form from a stored document, including one saved before `3c-0`, through the same reads
 * the engine uses (`readDemandSourceCost`, `readDemandSourcePrice`).
 */
export function termsFormFromStored(
  terms: StoredDemandSourceTerms | null | undefined,
  /**
   * The stored `restrictions` beside the terms (`5c-i`). **Required, `null` where there are none** —
   * a caller that forgot would hydrate an empty restrictions form and its next save would clear what
   * was stored (`#1134`).
   */
  restrictions: unknown,
): TermsFormValue {
  const cost = readDemandSourceCost(terms);
  const price = readDemandSourcePrice(terms?.price);
  const bounds = statedBounds(restrictions) ?? {};
  return {
    // A stored `false` is a statement: `3c-i`'s repair (`R8`) dropped the old class default at rest,
    // and the write path has kept an authored one since.
    required: typeof terms?.required === 'boolean' ? terms.required : undefined,
    cost: {
      type: cost?.type ?? 'cpm',
      value: cost?.value,
      currency: cost?.currency ?? (terms?.currency?.trim() || 'USD'),
    },
    price: {
      type: price?.type ?? 'floor',
      value: price?.value,
      currency: price?.currency ?? 'USD',
    },
    weight: finite(terms?.weight),
    maxFetchRounds: finite(terms?.maxFetchRounds),
    restrictions: {
      minDurationSeconds: bounds.minDurationSeconds,
      maxDurationSeconds: bounds.maxDurationSeconds,
      allowIabCategories: bounds.allowIabCategories ?? [],
      excludeIabCategories: bounds.excludeIabCategories ?? [],
      excludeAdvertisers: bounds.excludeAdvertisers ?? [],
      excludeDomains: bounds.excludeDomains ?? [],
      allowRepeatAds: bounds.allowRepeatAds,
    },
  };
}

/**
 * **What a save sends as the tier's restrictions** — only what it states, through the same reader the
 * server stores with (`statedBounds`), so a blank member is omitted and an empty allow-list states
 * nothing. `{}` rather than `undefined` when nothing is stated: *Standard Terms'* route reads an absent
 * body member as *leave it alone*, and clearing every restriction is a statement.
 */
export function restrictionsToPayload(form: TermsFormValue): PartialBounds {
  const r = form.restrictions;
  return (
    statedBounds({
      minDurationSeconds: r.minDurationSeconds,
      maxDurationSeconds: r.maxDurationSeconds,
      allowIabCategories: r.allowIabCategories.length > 0 ? r.allowIabCategories : undefined,
      excludeIabCategories: r.excludeIabCategories,
      excludeAdvertisers: r.excludeAdvertisers,
      excludeDomains: r.excludeDomains,
      allowRepeatAds: r.allowRepeatAds,
    }) ?? {}
  );
}

export function termsToPayload(form: TermsFormValue): DemandSourceTermsPayload {
  const payload: DemandSourceTermsPayload = {};
  if (form.required !== undefined) payload.required = form.required;
  const { type, value, currency } = form.cost;
  if (value !== undefined && Number.isFinite(value)) {
    payload.cost = { type, value, currency: currency.trim() || 'USD' };
  }
  if (form.price.value !== undefined && Number.isFinite(form.price.value)) {
    payload.price = { type: form.price.type, value: form.price.value, currency: form.price.currency.trim() || 'USD' };
  }
  if (form.weight !== undefined && Number.isFinite(form.weight)) payload.weight = form.weight;
  if (form.maxFetchRounds !== undefined && Number.isFinite(form.maxFetchRounds)) {
    payload.maxFetchRounds = form.maxFetchRounds;
  }
  return payload;
}

function finite(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Why the form's terms **cannot be saved**, or `null` when they can — the shared rule
 * (`termsValueProblem`), read off what the form would send, so the box and the server refuse the same
 * values in the same words.
 */
export function termsFormProblem(form: TermsFormValue): string | null {
  return termsValueProblem(termsToPayload(form)) ?? boundsValueProblem(restrictionsToPayload(form));
}

/* ------------------------------------------------------------------ *
 * What a blank member inherits (`3c-ii`, `#1398`)
 * ------------------------------------------------------------------ */

/** Whose terms the box is editing, for the sentences that name it. */
export type TermsSubject = 'source' | 'standard';

/** One member as the tier beneath would answer it. */
export type InheritedTerm<K extends TermMember = TermMember> = {
  value: NonNullable<PartialTerms[K]>;
  from: TermsTierRef;
};

/**
 * **What is in force beneath the tier being edited** — what each member resolves to if this tier
 * states nothing, and the tiers that answer, least specific first.
 *
 * Required on the box, because `ST2` makes the box exactly the resolved tier: a blank member there is
 * not *no value*, it is *whatever is beneath*, and an editor that could not say what that is would
 * teach the operator the box is empty (*a derived value gets a default, and the one in force is
 * shown*).
 */
export interface InheritedTerms {
  terms: { [K in TermMember]?: InheritedTerm<K> };
  /**
   * **What the tiers beneath combine to, per restriction** (`5c-i`) — what a blank restriction inherits,
   * and what a stated one is compared against to say whether it loosens.
   */
  bounds: { [K in BoundMember]?: ResolvedBoundValue<NonNullable<PartialBounds[K]>> };
  tiers: readonly TermsTierRef[];
}

/** Nothing beneath — the base tier, where a blank member is stated nowhere. */
export const NOTHING_INHERITED: InheritedTerms = Object.freeze({ terms: {}, bounds: {}, tiers: [] }) as InheritedTerms;

/**
 * From a **stored source's** resolution: where the source won, what it overrides (`beneath`); where
 * it said nothing, the winner. A source's own tier is never beneath itself.
 */
export function inheritedFromResolved(resolved: ResolvedTerms | undefined): InheritedTerms {
  if (!resolved) return NOTHING_INHERITED;
  const terms: InheritedTerms['terms'] = {};
  for (const member of TERM_MEMBERS) {
    const entry = resolved.terms[member];
    if (!entry) continue;
    const answer = entry.from.kind === 'source' ? entry.beneath : { value: entry.value, from: entry.from };
    if (answer) (terms as Record<string, unknown>)[member] = answer;
  }
  // A bound's `from` is every tier that contributed. Where the source is one of them, what is beneath is
  // the combination without it (`beneath`); where it is not, the whole value is beneath.
  const bounds: InheritedTerms['bounds'] = {};
  for (const member of BOUND_MEMBERS) {
    const entry = resolved.bounds[member];
    if (!entry) continue;
    const fromSource = entry.from.some((ref) => ref.kind === 'source');
    const answer = fromSource ? entry.beneath : { value: entry.value, from: entry.from };
    if (answer) (bounds as Record<string, unknown>)[member] = answer;
  }
  return { terms, bounds, tiers: resolved.tiers.filter((tier) => tier.kind !== 'source') };
}

/**
 * For a source **not yet saved**, which has no resolution: Standard Terms' own statement, resolved by
 * the same function the read boundary uses. A new source names no partner (the picker is `#837`'s), so
 * Standard Terms is the whole of what is beneath it.
 */
export function inheritedFromStandardTerms(
  standard: { id?: string; name?: string; terms?: PartialTerms; restrictions?: unknown } | null | undefined,
): InheritedTerms {
  if (!standard) return NOTHING_INHERITED;
  const ref: TermsTierRef = {
    kind: 'standard',
    key: standard.id ?? STANDARD_TERMS_KEY,
    label: standard.name?.trim() || STANDARD_TERMS_NAME,
  };
  return inheritedFromResolved(
    resolveTerms([{ ref, terms: standard.terms, bounds: statedBounds(standard.restrictions) }]),
  );
}

/** One term's value as an operator reads it — the words every mark and the detail page use. */
export function describeTerm(member: TermMember, value: unknown): string {
  switch (member) {
    case 'cost':
      return describeDemandSourceCost(value as DemandSourceCost);
    case 'price':
      return describeDemandSourcePrice(value as DemandSourcePrice);
    case 'weight':
      return describeDemandSourceWeight(value as number);
    case 'required':
      return value ? 'required' : 'not required';
    case 'maxFetchRounds':
      return `${value as number} per pod`;
    default:
      return String(value);
  }
}

/**
 * **The in-force mark for one member of the box** — `null` when the value is the tier's own and
 * overrides nothing, since a filled box already says that.
 *
 * - blank, something beneath → *From Standard Terms: 20% of the bid*
 * - stated, something beneath → *Overrides Standard Terms: 20% of the bid*
 */
export function termMarkText(member: TermMember, stated: boolean, inherited: InheritedTerm | undefined): string | null {
  if (!inherited) return null;
  const said = `${inherited.from.label}: ${describeTerm(member, inherited.value)}`;
  return stated ? `Overrides ${said}` : `From ${said}`;
}

/** Which members the form **states** — the other half of every mark. */
export function statedMembers(form: TermsFormValue): ReadonlySet<TermMember> {
  return new Set(Object.keys(termsToPayload(form)) as TermMember[]);
}

/* ------------------------------------------------------------------ *
 * The terms in force, read-only (`3c-ii`, `#1398`)
 * ------------------------------------------------------------------ */

/** One member as the detail page shows it: the value in force, and where it came from. */
export interface InForceTermRow {
  member: TermMember;
  label: string;
  value: string;
  /** *From Standard Terms*, or *Overrides Acme: 30% of the bid*; `null` for the source's own value. */
  mark: string | null;
}

/**
 * **What is in force for a source, member by member** — read from the resolution the server attached,
 * which is what the engine reads (`effectiveTerms`). The detail page used to show what the source
 * **stated**, so a source buying on Standard Terms read as having no cost while the optimizer valued its
 * bids at the Standard Terms rate.
 *
 * *None* is said only where no tier states the member, and says what that means.
 */
export function inForceTermRows(resolved: ResolvedTerms): InForceTermRow[] {
  return TERM_MEMBERS.map((member) => {
    const entry = resolved.terms[member];
    const label =
      member === 'price'
        ? entry
          ? DEMAND_SOURCE_PRICE_TYPE_LABELS[(entry.value as DemandSourcePrice).type]
          : 'Price'
        : TERM_LABELS[member];
    if (!entry) return { member, label, value: NONE_IN_FORCE[member], mark: null };
    const value =
      member === 'required' ? (entry.value ? 'Required' : 'Not required') : describeTerm(member, entry.value);
    const mark =
      entry.from.kind !== 'source'
        ? `From ${entry.from.label}`
        : entry.beneath
          ? `Overrides ${entry.beneath.from.label}: ${describeTerm(member, entry.beneath.value)}`
          : null;
    return { member, label, value, mark };
  });
}

/**
 * **The box's one statement of where its values come from** (`ST2`). Named most specific first,
 * because that is the order a blank member is answered in.
 */
export function provenanceSentence(subject: TermsSubject, inherited: InheritedTerms): string {
  if (subject === 'standard') {
    return 'The terms every demand source starts from. A source, or the partner it buys under, may state its own; a term left blank here is stated nowhere.';
  }
  if (inherited.tiers.length === 0) {
    return 'A term left blank is stated nowhere, so the source has none.';
  }
  const names = [...inherited.tiers].reverse().map((tier) => tier.label);
  return `A term left blank takes the value in force from ${names.join(', then ')}.`;
}

/** The read view's one statement of where a source's terms come from. */
export function inForceProvenance(resolved: ResolvedTerms): string {
  const beneath = resolved.tiers.filter((tier) => tier.kind !== 'source');
  if (beneath.length === 0) return 'Only what this source states is in force; nothing beneath it states a term.';
  const names = [...beneath].reverse().map((tier) => tier.label);
  return `Where this source states nothing, the value in force comes from ${names.join(', then ')}.`;
}

const TERM_LABELS: Readonly<Record<TermMember, string>> = {
  cost: 'Cost',
  price: 'Price',
  weight: 'Weight',
  required: 'Required',
  maxFetchRounds: 'Max Fetch Rounds',
};

/** What each member means when **no tier** states it — the only place *none* may be said. */
const NONE_IN_FORCE: Readonly<Record<TermMember, string>> = {
  cost: 'None — bids are valued at the full bid',
  price: 'None',
  weight: 'No boost',
  required: 'Not required',
  maxFetchRounds: 'Unlimited',
};

/* ------------------------------------------------------------------ *
 * Ad restrictions (`5c-i`, `#1321`)
 * ------------------------------------------------------------------ */

/** What an operator calls each restriction — one label for the box, the detail page and the marks. */
export const BOUND_LABELS: Readonly<Record<BoundMember, string>> = {
  minDurationSeconds: 'Shortest ad (s)',
  maxDurationSeconds: 'Longest ad (s)',
  allowIabCategories: 'Only these categories',
  excludeIabCategories: 'Blocked categories',
  excludeAdvertisers: 'Blocked advertisers',
  excludeDomains: 'Blocked domains',
  allowRepeatAds: 'Repeat ads',
};

/** One restriction's value as an operator reads it. */
export function describeBound(member: BoundMember, value: unknown): string {
  switch (member) {
    case 'minDurationSeconds':
      return `at least ${value as number}s`;
    case 'maxDurationSeconds':
      return `at most ${value as number}s`;
    case 'allowIabCategories': {
      const list = value as readonly string[];
      return list.length === 0 ? 'no category may run' : `only ${list.join(', ')}`;
    }
    case 'allowRepeatAds':
      return value ? 'allowed' : 'not allowed';
    default:
      return (value as readonly string[]).join(', ');
  }
}

/**
 * **The mark under one restriction in the box.**
 *
 * - blank, something beneath → *From Standard Terms: at most 60s*
 * - stated and looser than beneath → `describeLoosening`'s sentence: *… is not applied — a bound may be
 *   made stricter, not looser*
 * - stated and stricter → *In force — stricter than Standard Terms (at most 60s)*. **The mark says that
 *   the value in the box is the one that applies, then names what it tightens.** It first read *Combined
 *   with Standard Terms: at most 60s*, which named the losing value under a winning one, so a 20s
 *   override over a 60s base read as though 60s were in force (Todd, rehearsing `5c-i`).
 * - stated and equal → *Same as Standard Terms (at most 60s)*
 * - an exclusion list → *Standard Terms also blocks IAB7* — a union is in force as a whole, so the mark
 *   names what is added beneath rather than claiming either list wins
 *
 * Repeat ads say what an unstated answer **is**, because it is the one bound whose absence is the
 * restrictive side (Todd, 2026-09-21) and a blank select would otherwise read as *no rule*.
 */
export function boundMarkText(
  member: BoundMember,
  stated: PartialBounds[BoundMember],
  inherited: ResolvedBoundValue<unknown> | undefined,
): string | null {
  if (inherited) {
    const said = `${inherited.from.map((ref) => ref.label).join(', ')}: ${describeBound(member, inherited.value)}`;
    if (stated === undefined) return `From ${said}`;
    const loosening = describeLoosening(member, stated, inherited);
    if (loosening) return loosening;
    const who = inherited.from.map((ref) => ref.label).join(', ');
    const theirs = describeBound(member, inherited.value);
    if (BOUND_RULES[member] === 'union') {
      const mine = new Set(((stated as readonly string[] | undefined) ?? []).map((e) => e.toLowerCase()));
      const extra = (inherited.value as readonly string[]).filter((e) => !mine.has(e.toLowerCase()));
      return extra.length > 0 ? `${who} also blocks ${extra.join(', ')}` : null;
    }
    const same =
      BOUND_RULES[member] === 'intersection'
        ? sameMembers(stated as readonly string[], inherited.value as readonly string[])
        : stated === inherited.value;
    return same ? `Same as ${who} (${theirs})` : `In force — stricter than ${who} (${theirs})`;
  }
  if (member === 'allowRepeatAds' && stated === undefined)
    return 'Nothing states otherwise, so an ad runs at most once per break.';
  return null;
}

/** One restriction as the detail page shows it: the value in force, and every tier behind it. */
export interface InForceBoundRow {
  member: BoundMember;
  label: string;
  value: string;
  /** *From Standard Terms, Acme* where a tier beneath contributed; `null` for the source's own alone. */
  mark: string | null;
}

/**
 * **What restricts this source's ads, member by member** — its own and the tiers beneath, combined.
 * What restricts the **break** also includes every other selected source's, which no single source's
 * page can show; the section says so.
 */
export function inForceBoundRows(resolved: ResolvedTerms): InForceBoundRow[] {
  return BOUND_MEMBERS.map((member) => {
    const entry = resolved.bounds[member];
    const label = BOUND_LABELS[member];
    if (!entry) return { member, label, value: NONE_BOUND[member], mark: null };
    const others = entry.from.filter((ref) => ref.kind !== 'source').map((ref) => ref.label);
    return {
      member,
      label,
      // A sentence-shaped value starts with a capital; a list of domains or codes is shown as written,
      // because `Blocked.com` is not the domain the operator blocked.
      value:
        BOUND_RULES[member] === 'union'
          ? describeBound(member, entry.value)
          : capitalise(describeBound(member, entry.value)),
      mark: others.length ? `From ${others.join(', ')}` : null,
    };
  });
}

/** What each restriction means when **no tier** states it. */
const NONE_BOUND: Readonly<Record<BoundMember, string>> = {
  minDurationSeconds: 'No minimum',
  maxDurationSeconds: 'No maximum',
  allowIabCategories: 'Every category',
  excludeIabCategories: 'None',
  excludeAdvertisers: 'None',
  excludeDomains: 'None',
  allowRepeatAds: 'Not allowed',
};

/**
 * **Where a combination of tiers leaves no ad able to run** — a warning, never a block: each tier's
 * value is valid, and the combination is a fact about the tiers together (*block on an invalid value,
 * never on a missing one*).
 */
export function boundsConflict(form: TermsFormValue, inherited: InheritedTerms): string | null {
  const beneath: PartialBounds = {};
  for (const member of BOUND_MEMBERS) {
    const entry = inherited.bounds[member];
    if (entry) (beneath as Record<string, unknown>)[member] = entry.value;
  }
  const combined = combineBounds([
    { ref: { kind: 'standard', label: 'beneath' }, bounds: beneath },
    { ref: { kind: 'source', label: 'this' }, bounds: restrictionsToPayload(form) },
  ]);
  const min = combined.minDurationSeconds?.value;
  const max = combined.maxDurationSeconds?.value;
  if (min !== undefined && max !== undefined && min > max) {
    return `With what is beneath, the shortest ad allowed (${min}s) is longer than the longest (${max}s), so no ad can run.`;
  }
  if (combined.allowIabCategories && combined.allowIabCategories.value.length === 0) {
    return 'With what is beneath, the allowed categories share nothing, so no categorised ad can run.';
  }
  return null;
}

/**
 * **The non-blocking findings on the Terms box's own members** (`3h`, `#1456`), drawn beside the
 * member by the box itself, so the wizard, the detail page and Standard Terms all show them.
 *
 * One today: a **fixed price with no number** states no price — the tick-without-a-number case `#1097`
 * blocked, which is a **missing** value and so an error rather than a block (`R3a`). It says what is
 * **in force**, not what this tier states: the optimizer reads the resolved terms, so a blank member
 * under Standard Terms or a partner is not *none* (`3c-ii`).
 *
 * *No cost stated anywhere* was a wizard advisory too, and is not here: the box's cost preview already
 * says it in the same words, beside the same field, and one sentence twice on one screen is the
 * `#1254` defect.
 */
export function termsFieldFindings(
  form: TermsFormValue,
  inherited: InheritedTerms,
): Partial<Record<TermMember, FieldFinding>> {
  const out: Partial<Record<TermMember, FieldFinding>> = {};
  if (form.price.type === 'fixed' && form.price.value === undefined) {
    const inheritedPrice = inherited.terms.price;
    out.price = {
      level: 'error',
      message: inheritedPrice
        ? `Fixed Price has no value, so this source states no price and ${inheritedPrice.from.label}'s ${describeTerm('price', inheritedPrice.value)} is in force.`
        : 'Fixed Price has no value, so no price is in force: bids are recorded as returned.',
    };
  }
  return out;
}

/**
 * **The restrictions' findings, in the mapping editors' shape** (`5c-i` rehearsal) — so the box renders
 * them through `ValidationSummary`, the treatment every mapping editor uses, rather than as a sentence
 * at the foot of the box that an operator could scroll past (Todd: *"the message appears at the bottom
 * of the box and isn't obvious"*).
 *
 * Two levels, and the difference is `block on an invalid value, never on a missing one`: a minimum above
 * the maximum **this tier** states is this tier's mistake and blocks the save (`error`); the same clash
 * produced only by combining with a tier beneath is a fact about the tiers together, and warns.
 */
export function restrictionIssues(form: TermsFormValue, inherited: InheritedTerms): MappingIssue[] {
  const problem = boundsValueFinding(restrictionsToPayload(form));
  if (problem) return [{ kind: problem.kind, message: problem.message, ref: restrictionRefFor(problem.message) }];
  const conflict = boundsConflict(form, inherited);
  return conflict ? [{ kind: 'conflict', message: conflict, ref: restrictionRefFor(conflict) }] : [];
}

/** Which control a restriction finding is about, for the list's mono reference. */
function restrictionRefFor(message: string): string {
  if (/categor/i.test(message)) return BOUND_LABELS.allowIabCategories;
  return `${BOUND_LABELS.minDurationSeconds} · ${BOUND_LABELS.maxDurationSeconds}`;
}

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a.map((e) => e.toLowerCase()));
  const right = new Set(b.map((e) => e.toLowerCase()));
  return left.size === right.size && [...left].every((e) => right.has(e));
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
